import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MapPin } from 'lucide-react';
import LocationPicker from './LocationPicker';

export default function EditClubModal({ club, onClose, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(
    club.latitude && club.longitude ? {
      lat: club.latitude,
      lng: club.longitude,
      address: club.formatted_address || ''
    } : null
  );
  const [formData, setFormData] = useState({
    name: club.name || '',
    description: club.description || ''
  });

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = {
      name: formData.name,
      description: formData.description,
      latitude: selectedLocation?.lat || null,
      longitude: selectedLocation?.lng || null,
      formatted_address: selectedLocation?.address || ''
    };

    try {
      await axios.put(`/clubs/${club.id}`, data);
      toast.success('Club updated successfully!');
      onUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update club');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!showLocationPicker && (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Club</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Club Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormField('name', e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => updateFormField('description', e.target.value)}
                  required
                  rows={4}
                />
              </div>

              <div>
                <Label>Club Location</Label>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-1 justify-start text-left h-auto py-3"
                  onClick={() => setShowLocationPicker(true)}
                >
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0 text-purple-500" />
                  {selectedLocation ? (
                    <span className="truncate text-foreground">{selectedLocation.address}</span>
                  ) : (
                    <span className="text-muted-foreground">Tap to set club location</span>
                  )}
                </Button>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={selectedLocation}
      />
    </>
  );
}
